import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { colorPrimary } from '../../Styles/Colors';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    minWidth: '400px',
    maxWidth: '500px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  padding: '24px 24px 16px 24px',
  fontSize: '20px',
  fontWeight: 600,
  color: '#424242',
  position: 'relative',
  '& .MuiIconButton-root': {
    position: 'absolute',
    right: '16px',
    top: '16px',
    color: '#666666',
  },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: '0 24px 16px 24px',
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: '16px 24px 24px 24px',
  gap: '12px',
}));

const ActionButton = styled(Button)(({ variant: buttonVariant, color }) => ({
  borderRadius: '8px',
  textTransform: 'none',
  fontSize: '14px',
  fontWeight: 500,
  padding: '10px 24px',
  minWidth: '80px',
  ...(buttonVariant === 'contained' && {
    backgroundColor: color === 'primary' || color === colorPrimary ? colorPrimary : '#E0E0E0',
    color: color === 'primary' || color === colorPrimary ? '#FFFFFF' : '#424242',
    '&:hover': {
      backgroundColor: color === 'primary' || color === colorPrimary ? '#4a0f3d' : '#BDBDBD',
    },
  }),
  ...(buttonVariant === 'outlined' && {
    border: `1px solid ${color === 'primary' || color === colorPrimary ? colorPrimary : '#E0E0E0'}`,
    color: color === 'primary' || color === colorPrimary ? colorPrimary : '#424242',
    '&:hover': {
      backgroundColor: color === 'primary' || color === colorPrimary ? '#f3e5f5' : '#F5F5F5',
      border: `1px solid ${color === 'primary' || color === colorPrimary ? '#4a0f3d' : '#BDBDBD'}`,
    },
  }),
  ...(buttonVariant === 'text' && {
    color: color === 'primary' || color === colorPrimary ? colorPrimary : '#424242',
    '&:hover': {
      backgroundColor: color === 'primary' || color === colorPrimary ? '#f3e5f5' : '#F5F5F5',
    },
  }),
}));

const HeroIcon = styled(Box)(({ iconColor }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: '16px',
  '& .MuiSvgIcon-root': {
    fontSize: '48px',
    color: iconColor || '#9C27B0',
  },
}));

const BasicDialog = ({
  open = false,
  onClose,
  title,
  content = "A dialog is a type of modal window that appears in front of app content to provide critical information, or prompt for a decision to be made.",
  type = "basic", // basic, success, error, warning, info
  actions = [
    { label: "Cancel", variant: "outlined", color: "secondary", onClick: () => {} },
    { label: "OK", variant: "contained", color: "primary", onClick: () => {} }
  ],
  showCloseButton = true,
  maxWidth = "sm",
  style,
  ...props
}) => {
  const handleClose = () => {
    if (onClose) onClose();
  };

  const handleActionClick = (action) => {
    if (action.onClick) action.onClick();
    if (action.closeOnClick !== false) handleClose();
  };

  // Get type-specific configuration
  const getTypeConfig = () => {
    switch (type) {
      case 'success':
        return {
          icon: <CheckCircleIcon />,
          iconColor: '#4CAF50',
          title: title || 'Success'
        };
      case 'error':
        return {
          icon: <ErrorIcon />,
          iconColor: '#F44336',
          title: title || 'Error'
        };
      case 'warning':
        return {
          icon: <WarningIcon />,
          iconColor: '#FF9800',
          title: title || 'Warning'
        };
      case 'info':
        return {
          icon: <InfoIcon />,
          iconColor: '#2196F3',
          title: title || 'Information'
        };
      default:
        return {
          icon: null,
          iconColor: '#9C27B0',
          title: title || 'Dialog'
        };
    }
  };

  const typeConfig = getTypeConfig();
  const showIcon = type !== 'basic' && typeConfig.icon;

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth
      style={style}
      {...props}
    >
      <StyledDialogTitle>
        {typeConfig.title}
        {showCloseButton && (
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </StyledDialogTitle>
      
      <StyledDialogContent>
        {showIcon && (
          <HeroIcon iconColor={typeConfig.iconColor}>
            {typeConfig.icon}
          </HeroIcon>
        )}
        <Typography 
          variant="body1" 
          color="text.secondary" 
          sx={{ 
            lineHeight: 1.6,
            textAlign: showIcon ? 'center' : 'left'
          }}
        >
          {content}
        </Typography>
      </StyledDialogContent>
      
      {actions && actions.length > 0 && (
        <StyledDialogActions>
          {actions.map((action, index) => (
            <ActionButton
              key={index}
              variant={action.variant || "contained"}
              color={action.color || "primary"}
              onClick={() => handleActionClick(action)}
              disabled={action.disabled}
            >
              {action.label}
            </ActionButton>
          ))}
        </StyledDialogActions>
      )}
    </StyledDialog>
  );
};

// Preset dialog configurations
export const DialogPresets = {
  success: (message, onOk) => ({
    type: 'success',
    title: 'Success',
    content: message,
    actions: [{ label: 'OK', variant: 'contained', color: 'primary', onClick: onOk }]
  }),
  
  error: (message, onOk) => ({
    type: 'error', 
    title: 'Error',
    content: message,
    actions: [{ label: 'OK', variant: 'contained', color: 'primary', onClick: onOk }]
  }),
  
  warning: (message, onCancel, onConfirm) => ({
    type: 'warning',
    title: 'Warning',
    content: message,
    actions: [
      { label: 'Cancel', variant: 'outlined', color: 'secondary', onClick: onCancel },
      { label: 'Confirm', variant: 'contained', color: 'primary', onClick: onConfirm }
    ]
  }),
  
  info: (message, onOk) => ({
    type: 'info',
    title: 'Information', 
    content: message,
    actions: [{ label: 'Got it', variant: 'contained', color: 'primary', onClick: onOk }]
  }),
  
  confirm: (message, onCancel, onConfirm) => ({
    type: 'basic',
    title: 'Confirm Action',
    content: message,
    actions: [
      { label: 'Cancel', variant: 'outlined', color: 'secondary', onClick: onCancel },
      { label: 'Confirm', variant: 'contained', color: 'primary', onClick: onConfirm }
    ]
  })
};

// Utility function for backward compatibility
export const showDialog = {
  success: ({ title, content, actions }) => ({
    type: 'success',
    title,
    content,
    actions
  }),
  error: ({ title, content, actions }) => ({
    type: 'error',
    title,
    content,
    actions
  }),
  warning: ({ title, content, actions }) => ({
    type: 'warning',
    title,
    content,
    actions
  }),
  info: ({ title, content, actions }) => ({
    type: 'info',
    title,
    content,
    actions
  }),
  basic: ({ title, content, actions }) => ({
    type: 'basic',
    title,
    content,
    actions
  })
};

export default BasicDialog;
